import React, { useEffect, useState } from "react";
import {
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { BootstrapTooltip } from "../Tooltip";
import {
  FirstPageIcon,
  LastPageIcon,
  NextPageIcon,
  PrevPageIcon,
  ZoomInIcon,
  ZoomOutIcon,
  DownloadIcon,
  RedirectIcon,
  RotateClockwiseIcon
} from "../../create-loan/Icons";
import {
  getZoomSelectLabel,
  getZoomSelectValue,
  PDF_ZOOM_PERCENT_PRESETS,
  PDF_ZOOM_PERCENT_STEP,
  PdfZoomState
} from "./pdfZoom";

export type PDFViewerToolbarProps = {
  classes: Record<string, string>;
  isFirstPage: boolean;
  isLastPage: boolean;
  currentPage: number;
  totalPages: number;
  pdfZoom: PdfZoomState;
  isZoomPercentMin: boolean;
  isZoomPercentMax: boolean;
  isFullscreen: boolean;
  highlightChecked?: boolean;
  highlightDisabled?: boolean;
  useCache: boolean;
  pdfData: string | null;
  onSetPageFromControl: (pageNo: number, pageCount?: number) => void;
  onBumpZoomPercent: (delta: number) => void;
  onZoomSelectChange: (value: string) => void;
  onToggleFullscreen: () => void;
  onRotateCounterClockwise: () => void;
  onRotate: () => void;
  onOpenInNewTab: () => void;
  onDownload: (downloadUrl: string) => void;
  onChangeHighlightSelect?: Function;
};

export const PDFViewerToolbar = (props: PDFViewerToolbarProps) => {
  const {
    classes,
    isFirstPage,
    isLastPage,
    currentPage,
    totalPages,
    pdfZoom,
    isZoomPercentMin,
    isZoomPercentMax,
    isFullscreen,
    highlightChecked,
    highlightDisabled,
    useCache,
    pdfData,
    onSetPageFromControl,
    onBumpZoomPercent,
    onZoomSelectChange,
    onToggleFullscreen,
    onRotateCounterClockwise,
    onRotate,
    onOpenInNewTab,
    onDownload,
    onChangeHighlightSelect
  } = props;

  const [pageInputValue, setPageInputValue] = useState(String(currentPage));

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  const commitPageInput = () => {
    const val = Number(pageInputValue || 1);
    if (Number.isNaN(val) || totalPages < 1) {
      setPageInputValue(String(currentPage));
      return;
    }
    onSetPageFromControl(val, totalPages);
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitPageInput();
      return;
    }

    const isPrevKey = e.key === "ArrowUp" || e.key === "ArrowLeft";
    const isNextKey = e.key === "ArrowDown" || e.key === "ArrowRight";
    if (!isPrevKey && !isNextKey) return;

    e.preventDefault();
    if (totalPages < 1) return;

    const nextPage = isPrevKey
      ? Math.max(1, currentPage - 1)
      : Math.min(totalPages, currentPage + 1);
    if (nextPage === currentPage) return;

    setPageInputValue(String(nextPage));
    onSetPageFromControl(nextPage, totalPages);
  };

  return (
    <div
      className={`${classes.controlsBox} ${isFullscreen ? classes.controlsBoxFullscreen : ""}`}
    >
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isFirstPage ? "" : "First page"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="first-page"
              data-testid="first-page"
              onClick={() => onSetPageFromControl(1)}
              className={classes.toolbarIcon}
              disabled={isFirstPage}
              size="large"
            >
              <FirstPageIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isFirstPage ? "" : "Previous page"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="previous-page"
              data-testid="previous-page"
              onClick={() => onSetPageFromControl(Math.max(1, currentPage - 1))}
              className={classes.toolbarIcon}
              disabled={isFirstPage}
              size="large"
            >
              <PrevPageIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div className={classes.pageInput}>
        <input
          value={pageInputValue}
          onChange={(e) => setPageInputValue(e.target.value)}
          onBlur={commitPageInput}
          onKeyDown={handlePageInputKeyDown}
          style={{ width: 48, marginRight: 8 }}
        />
        <span style={{ marginRight: 4 }}>/</span>
        <span>{totalPages || 0}</span>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isLastPage ? "" : "Next page"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="next-page"
              data-testid="next-page"
              onClick={() => onSetPageFromControl(currentPage + 1)}
              className={classes.toolbarIcon}
              disabled={isLastPage}
              size="large"
            >
              <NextPageIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isLastPage ? "" : "Last page"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="last-page"
              data-testid="last-page"
              onClick={() => onSetPageFromControl(totalPages)}
              className={classes.toolbarIcon}
              disabled={isLastPage}
              size="large"
            >
              <LastPageIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isZoomPercentMin ? "" : "Zoom out"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="zoom-out"
              data-testid="zoom-out"
              onClick={() => onBumpZoomPercent(-PDF_ZOOM_PERCENT_STEP)}
              className={`${classes.toolbarIcon} ${classes.zoomOut}`}
              disabled={isZoomPercentMin}
              size="large"
            >
              <ZoomOutIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <FormControl size="small" className={classes.pdfZoomSelect}>
          <Select
            value={getZoomSelectValue(pdfZoom)}
            onChange={(e) => onZoomSelectChange(e.target.value)}
            displayEmpty
            renderValue={() => getZoomSelectLabel(pdfZoom)}
            MenuProps={{
              disablePortal: isFullscreen,
              ...(isFullscreen && {
                PaperProps: { sx: { zIndex: 1401 } }
              })
            }}
          >
            <MenuItem value="fit-width">Fit to width</MenuItem>
            <MenuItem value="fit-page">Fit to page</MenuItem>
            {PDF_ZOOM_PERCENT_PRESETS.map((preset) => (
              <MenuItem key={preset} value={String(preset)}>
                {preset}%
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title={isZoomPercentMax ? "" : "Zoom in"} placement="bottom" arrow>
          <span>
            <IconButton
              aria-label="zoom-in"
              data-testid="zoom-in"
              onClick={() => onBumpZoomPercent(PDF_ZOOM_PERCENT_STEP)}
              className={classes.toolbarIcon}
              disabled={isZoomPercentMax}
              size="large"
            >
              <ZoomInIcon size="18" />
            </IconButton>
          </span>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px", paddingTop: 2 }}>
        <BootstrapTooltip title="Rotate counter-clockwise" placement="bottom" arrow>
          <IconButton
            aria-label="rotate-counter-clockwise"
            data-testid="rotate-counter-clockwise"
            onClick={onRotateCounterClockwise}
            className={classes.toolbarIcon}
            size="large"
          >
            <span style={{ display: "inline-flex", transform: "scaleX(-1)" }}>
              <RotateClockwiseIcon viewBox="0 3 16 16" />
            </span>
          </IconButton>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px", paddingTop: 2 }}>
        <BootstrapTooltip title="Rotate clockwise" placement="bottom" arrow>
          <IconButton
            aria-label="rotate-clockwise"
            data-testid="rotate-clockwise"
            onClick={onRotate}
            className={classes.toolbarIcon}
            size="large"
          >
            <RotateClockwiseIcon viewBox="0 3 16 16" />
          </IconButton>
        </BootstrapTooltip>
      </div>
      {highlightChecked != null && (
        <div style={{ padding: "0px 8px", position: "relative", top: -2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={highlightChecked}
                onChange={onChangeHighlightSelect as any}
                disabled={highlightDisabled}
              />
            }
            label="Highlight"
            className={classes.highlightCheckbox}
          />
        </div>
      )}
      {useCache && (
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title="Open in new tab" placement="bottom" arrow>
            <IconButton
              aria-label="open-in-new-tab"
              data-testid="open-in-new-tab"
              onClick={onOpenInNewTab}
              className={classes.toolbarIcon}
              size="large"
            >
              <RedirectIcon color="#183238" width={20} height={20} />
            </IconButton>
          </BootstrapTooltip>
        </div>
      )}
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip title="Download" placement="bottom" arrow>
          <IconButton
            aria-label="download"
            data-testid="download"
            onClick={() => onDownload(pdfData || "")}
            className={classes.toolbarIcon}
            size="large"
          >
            <DownloadIcon size="21" viewBox="0 1 16 16" />
          </IconButton>
        </BootstrapTooltip>
      </div>
      <div style={{ padding: "0px 2px" }}>
        <BootstrapTooltip
          title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          placement="bottom"
          arrow
        >
          <IconButton
            aria-label={isFullscreen ? "exit-fullscreen" : "fullscreen"}
            data-testid="fullscreen-toggle"
            onClick={onToggleFullscreen}
            className={classes.toolbarIcon}
            size="large"
          >
            <span
              style={{ display: "inline-flex", transform: "scale(1.45)", transformOrigin: "center" }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </span>
          </IconButton>
        </BootstrapTooltip>
      </div>
    </div>
  );
};
